import { useState } from "react";
import { CalendarDays, ChevronDown, Lightbulb, PenLine, Timer, Check } from "lucide-react";
import { fmtDue } from "@/lib/subjects";

const QCard = ({ q, i, onMark }) => {
  const [open, setOpen] = useState(false);
  return (
    <li data-testid={`pack-q-${i}`} className="rounded-2xl border border-slate-200 bg-surface p-4">
      <button data-testid={`pack-q-toggle-${i}`} onClick={() => setOpen(!open)} className="w-full flex items-start gap-3 text-left">
        <span className="grid place-items-center w-7 h-7 shrink-0 rounded-lg bg-navy-600 text-xs font-bold text-white">{i + 1}</span>
        <span className="flex-1 font-medium text-slate-900">{q.q}{q.topic && <span className="block mt-1 text-xs font-normal text-slate-500">{q.topic}</span>}</span>
        <ChevronDown className={`w-4 h-4 mt-1 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p data-testid={`pack-a-${i}`} className="mt-3 ml-10 rounded-xl border border-gold-400/40 bg-gold-400/10 p-3 text-sm text-slate-800"><Lightbulb className="inline w-4 h-4 mr-1 text-gold-300" />{q.a}</p>}
      {open && onMark && <button data-testid={`pack-q-mark-${i}`} onClick={() => onMark(q)} className="mt-2 ml-10 inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:text-white hover:border-gold-400/60"><PenLine className="w-3.5 h-3.5" />Answer it and get marked</button>}
    </li>
  );
};

export const PackView = ({ pack, onMark, onTick, onFocus }) => (
  <div className="space-y-6">
    {pack.summary && <p className="text-slate-700">{pack.summary}</p>}
    {(pack.topics ?? []).length > 0 && (
      <section data-testid="pack-topics">
        <h2 className="font-mono text-xs uppercase tracking-[0.18em] font-bold text-gold-300">Topics</h2>
        <div className="mt-2 flex flex-wrap gap-2">{pack.topics.map((t) => <span key={t} className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{t}</span>)}</div>
      </section>
    )}
    {(pack.timetable ?? []).length > 0 && (
      <section data-testid="pack-timetable">
        <h2 className="font-mono text-xs uppercase tracking-[0.18em] font-bold text-gold-300">Revision timetable{pack.test_date ? ` · test ${fmtDue(pack.test_date)}` : ""}</h2>
        <ul className="mt-2 space-y-2">
          {pack.timetable.map((s, idx) => (
            <li key={s.date} data-testid={`pack-session-${idx}`} className={`flex items-center gap-3 rounded-xl border border-slate-200 bg-surface px-3 py-2.5 text-sm ${s.done ? "opacity-60" : ""}`}>
              {onTick ? (
                <button data-testid={`pack-session-tick-${idx}`} onClick={() => onTick(idx, !s.done)} aria-label="Tick off session" className={`grid place-items-center w-6 h-6 shrink-0 rounded-md border ${s.done ? "bg-gold-400 border-gold-400 text-black" : "border-slate-300"}`}>{s.done && <Check className="w-4 h-4" />}</button>
              ) : <CalendarDays className="w-4 h-4 shrink-0 text-navy-700" />}
              <span className={`flex-1 text-slate-800 ${s.done ? "line-through" : ""}`}>{s.task}</span>
              <span className="shrink-0 font-mono text-xs text-slate-500">{fmtDue(s.date)} · {s.minutes}m</span>
              {onFocus && !s.done && <button data-testid={`pack-session-focus-${idx}`} onClick={() => onFocus(idx, s)} aria-label="Start focus timer" className="grid place-items-center w-8 h-8 shrink-0 rounded-lg border border-gold-400/50 text-gold-300 hover:bg-gold-400 hover:text-black"><Timer className="w-4 h-4" /></button>}
            </li>
          ))}
        </ul>
      </section>
    )}
    {(pack.questions ?? []).length > 0 && (
      <section data-testid="pack-questions">
        <h2 className="font-mono text-xs uppercase tracking-[0.18em] font-bold text-gold-300">Practice questions · tap to see the answer</h2>
        <ol className="mt-2 space-y-2">{pack.questions.map((q, i) => <QCard key={i} q={q} i={i} onMark={onMark} />)}</ol>
      </section>
    )}
  </div>
);
